import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Stack,
  Grid,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  EmojiEvents,
  ArrowUpward,
  Visibility,
  ContentCopy,
} from '@mui/icons-material';

interface Opportunity {
  pageId: number;
  pageUrl: string;
  type: 'quick-win' | 'serp-feature' | 'content-gap';
  title: string;
  description: string;
  currentPosition: number;
  targetPosition: number;
  estimatedImpact: 'high' | 'medium' | 'low';
  effort: 'low' | 'medium' | 'high';
  keyword?: string;
}

export const GrowthOpportunities: React.FC = () => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState<string>('all');

  const mockOpportunities: Opportunity[] = [
    {
      pageId: 1,
      pageUrl: 'https://zendigitalworld.com/greetings/',
      type: 'quick-win',
      title: 'Quick Win - Close to Top 3',
      description: 'Currently ranking #4, small improvements could push to top 3',
      currentPosition: 4,
      targetPosition: 3,
      estimatedImpact: 'high',
      effort: 'low',
      keyword: 'birthday greetings',
    },
    {
      pageId: 2,
      pageUrl: 'https://zendigitalworld.com/morning-messages/',
      type: 'serp-feature',
      title: 'SERP Feature Opportunity',
      description: 'Featured snippet opportunity detected for this query',
      currentPosition: 8,
      targetPosition: 1,
      estimatedImpact: 'high',
      effort: 'medium',
      keyword: 'morning wishes',
    },
    {
      pageId: 3,
      pageUrl: 'https://zendigitalworld.com/thank-you-messages/',
      type: 'content-gap',
      title: 'Content Gap - High Traffic Potential',
      description: 'GA4 data shows high engagement, but low GSC visibility',
      currentPosition: 15,
      targetPosition: 5,
      estimatedImpact: 'medium',
      effort: 'medium',
      keyword: 'thank you messages',
    },
  ];

  const getImpactColor = (impact: string): 'error' | 'warning' | 'success' => {
    switch (impact) {
      case 'high': return 'success';
      case 'medium': return 'warning';
      default: return 'error';
    }
  };

  const getEffortLabel = (effort: string) => {
    switch (effort) {
      case 'low': return '🟢 Low Effort';
      case 'medium': return '🟡 Medium Effort';
      case 'high': return '🔴 High Effort';
      default: return effort;
    }
  };

  const filteredOpportunities = selectedType === 'all'
    ? mockOpportunities
    : mockOpportunities.filter(o => o.type === selectedType);

  const quickWinCount = mockOpportunities.filter(o => o.type === 'quick-win').length;
  const serpFeatureCount = mockOpportunities.filter(o => o.type === 'serp-feature').length;
  const contentGapCount = mockOpportunities.filter(o => o.type === 'content-gap').length;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box display="flex" flexDirection="column" gap={3}>
        {/* Header */}
        <Box>
          <Typography variant="h3" component="h1" gutterBottom>
            💡 Growth Opportunities
          </Typography>
          <Typography variant="body1" color="text.secondary">
            High-potential pages with clear paths to improvement
          </Typography>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
              <CardActionArea onClick={() => setSelectedType('quick-win')}>
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h3">{quickWinCount}</Typography>
                      <Typography variant="body1">Quick Wins</Typography>
                      <Typography variant="caption">Close to top positions</Typography>
                    </Box>
                    <EmojiEvents sx={{ fontSize: 48, opacity: 0.7 }} />
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
              <CardActionArea onClick={() => setSelectedType('serp-feature')}>
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h3">{serpFeatureCount}</Typography>
                      <Typography variant="body1">SERP Features</Typography>
                      <Typography variant="caption">Feature opportunities</Typography>
                    </Box>
                    <Visibility sx={{ fontSize: 48, opacity: 0.7 }} />
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'secondary.light', color: 'secondary.contrastText' }}>
              <CardActionArea onClick={() => setSelectedType('content-gap')}>
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h3">{contentGapCount}</Typography>
                      <Typography variant="body1">Content Gaps</Typography>
                      <Typography variant="caption">High potential topics</Typography>
                    </Box>
                    <ContentCopy sx={{ fontSize: 48, opacity: 0.7 }} />
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>

        {/* Filter */}
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              Show:
            </Typography>
            <Chip
              label="All Opportunities"
              color={selectedType === 'all' ? 'primary' : 'default'}
              onClick={() => setSelectedType('all')}
              variant={selectedType === 'all' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Quick Wins"
              color={selectedType === 'quick-win' ? 'success' : 'default'}
              onClick={() => setSelectedType('quick-win')}
              variant={selectedType === 'quick-win' ? 'filled' : 'outlined'}
            />
            <Chip
              label="SERP Features"
              color={selectedType === 'serp-feature' ? 'info' : 'default'}
              onClick={() => setSelectedType('serp-feature')}
              variant={selectedType === 'serp-feature' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Content Gaps"
              color={selectedType === 'content-gap' ? 'secondary' : 'default'}
              onClick={() => setSelectedType('content-gap')}
              variant={selectedType === 'content-gap' ? 'filled' : 'outlined'}
            />
          </Stack>
        </Paper>

        {/* Opportunities List */}
        <Stack spacing={2}>
          {filteredOpportunities.map((opp) => (
            <Card key={opp.pageId} variant="outlined">
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <Stack spacing={2}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Chip
                          icon={<TrendingUp />}
                          label={opp.type.replace('-', ' ').toUpperCase()}
                          color={getImpactColor(opp.estimatedImpact)}
                          size="small"
                        />
                        <Typography variant="h6">{opp.title}</Typography>
                      </Stack>

                      <Typography
                        variant="body2"
                        color="primary.main"
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => navigate(`/pages/${opp.pageId}/changes`)}
                      >
                        {opp.pageUrl}
                      </Typography>

                      {opp.keyword && (
                        <Chip
                          label={`Keyword: "${opp.keyword}"`}
                          size="small"
                          variant="outlined"
                        />
                      )}

                      <Typography variant="body1">
                        {opp.description}
                      </Typography>

                      <Stack direction="row" spacing={3} alignItems="center">
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Current Position
                          </Typography>
                          <Typography variant="h5">#{opp.currentPosition}</Typography>
                        </Box>

                        <ArrowUpward color="success" />

                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Target Position
                          </Typography>
                          <Typography variant="h5" color="success.main">
                            #{opp.targetPosition}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Stack spacing={2}>
                      <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="body2" fontWeight="medium">
                            Estimated Impact
                          </Typography>
                          <Chip
                            label={opp.estimatedImpact.toUpperCase()}
                            color={getImpactColor(opp.estimatedImpact)}
                            size="small"
                          />
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={opp.estimatedImpact === 'high' ? 100 : opp.estimatedImpact === 'medium' ? 66 : 33}
                          color={getImpactColor(opp.estimatedImpact)}
                        />
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary" mb={1}>
                          Effort Required
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {getEffortLabel(opp.effort)}
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={1}>
                        <Tooltip title="View page details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/pages/${opp.pageId}/changes`)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Box>
    </Container>
  );
};

export default GrowthOpportunities;
